import { useEffect, useState } from 'react';
import { Lock, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 9.2 — Formulario de cambio de PIN.
 *
 * Cierra A07:2021: el handler verifica PIN actual con bcrypt + anti-IDOR
 * (sólo permite cambiar el PIN propio de la sesión). Esta UI valida
 * localmente para feedback inmediato y deja la autoridad al backend.
 *
 * Sprint 64 — Ya no es sólo del dueño. Con el PIN convertido en la credencial de los cuatro
 * oficios, dejar la rotación sólo al dueño significaba que el PIN de la cajera era una
 * contraseña compartida: la fijaba él, la conocía él, y se iba del negocio con quien
 * renunciara. Ahora cada quien puede cambiar el suyo —y sólo el suyo, eso lo sigue
 * verificando el servidor—.
 */

export function CambiarPinForm() {
  const sesion = useSessionStore((s) => s.sesion);
  const [pinActual, setPinActual] = useState('');
  const [pinNuevo, setPinNuevo] = useState('');
  const [pinConfirmacion, setPinConfirmacion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState<
    | { tipo: 'ok'; texto: string }
    | { tipo: 'error'; texto: string }
    | null
  >(null);
  const [ultimoCambio, setUltimoCambio] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Sprint 64 — La marca de auditoría es por usuario: antes una sola clave global hacía
      // que el cambio de la cajera pisara el registro del dueño.
      if (!sesion) return;
      const res = await window.api.config.obtener(`audit_pin_last_change_${sesion.usuarioId}`);
      if (alive && res.ok) setUltimoCambio(res.data);
    })();
    return () => {
      alive = false;
    };
  }, [sesion]);

  if (!sesion) {
    return (
      <div className="card max-w-md">
        <div className="flex items-center gap-2 text-slate-500">
          <ShieldCheck className="h-5 w-5" />
          <p className="text-sm">Inicia sesión para cambiar tu PIN.</p>
        </div>
      </div>
    );
  }

  // Validaciones locales (UX): el backend es autoridad.
  const formatoOk = /^\d{4}$/.test(pinActual) && /^\d{4}$/.test(pinNuevo);
  const coinciden = pinNuevo === pinConfirmacion;
  const distinto = pinNuevo !== pinActual;
  const puedeEnviar = formatoOk && coinciden && distinto && !enviando;

  const handleSubmit = async () => {
    setMensaje(null);
    if (!puedeEnviar || !sesion) return;
    setEnviando(true);
    const res = await window.api.usuarios.cambiarPin({
      usuarioId: sesion.usuarioId,
      pinActual,
      pinNuevo,
    });
    setEnviando(false);
    if (res.ok) {
      setPinActual('');
      setPinNuevo('');
      setPinConfirmacion('');
      setMensaje({ tipo: 'ok', texto: 'PIN cambiado correctamente.' });
      // Refrescar timestamp de auditoría
      const auditRes = await window.api.config.obtener(
        `audit_pin_last_change_${sesion.usuarioId}`,
      );
      if (auditRes.ok) setUltimoCambio(auditRes.data);
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  return (
    <div className="card max-w-md space-y-4">
      <div className="flex items-center gap-2">
        <Lock className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-bold">Cambiar PIN</h2>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>
          Si crees que tu PIN fue visto por alguien, cámbialo de inmediato. El sistema
          guarda solo la fecha del cambio, no los PINs.
        </p>
      </div>

      <PinInput
        id="pinActual"
        label="PIN actual"
        value={pinActual}
        onChange={setPinActual}
      />
      <PinInput
        id="pinNuevo"
        label="Nuevo PIN"
        value={pinNuevo}
        onChange={setPinNuevo}
        help="4 dígitos. Evita combinaciones obvias como 1234, 0000 o repeticiones."
      />
      <PinInput
        id="pinConfirmacion"
        label="Confirma el nuevo PIN"
        value={pinConfirmacion}
        onChange={setPinConfirmacion}
      />

      {pinNuevo && pinConfirmacion && !coinciden && (
        <p className="text-xs text-red-600">Los nuevos PIN no coinciden.</p>
      )}
      {pinActual && pinNuevo && !distinto && /^\d{4}$/.test(pinNuevo) && (
        <p className="text-xs text-red-600">El nuevo PIN debe ser distinto al actual.</p>
      )}

      {mensaje && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            mensaje.tipo === 'ok'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-700'
          }`}
          role="alert"
        >
          {mensaje.texto}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={!puedeEnviar}
        className="btn-primary w-full"
      >
        {enviando ? 'Cambiando…' : 'Cambiar PIN'}
      </button>

      {ultimoCambio && (
        <p className="text-xs text-slate-500 text-center">
          Último cambio: {formatearTimestamp(ultimoCambio)}
        </p>
      )}
    </div>
  );
}

function PinInput({
  id,
  label,
  value,
  onChange,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  help?: string;
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="password"
        inputMode="numeric"
        maxLength={4}
        autoComplete="new-password"
        className="input font-mono text-center text-2xl tracking-[0.5em]"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        placeholder="••••"
      />
      {help && <p className="help-text">{help}</p>}
    </div>
  );
}

function formatearTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
